import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  isTourCompletedInStorage,
  markTourCompletedInStorage,
  shouldForceProductTour,
} from '../components/onboarding/productTourStorage';
import { resolveTourId } from '../components/onboarding/tourUtils';
import ProductTourHost from '../components/onboarding/ProductTourHost';

const ProductTourContext = createContext(null);

function readTenantId() {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem('tenant_id') : null;
  } catch {
    return null;
  }
}

export const ProductTourProvider = ({ children }) => {
  const { user } = useAuth();
  const [isTourActive, setIsTourActive] = useState(false);
  const [pendingTourId, setPendingTourId] = useState(null);

  const buildTourContext = useCallback((overrides = {}) => {
    const tenantId = readTenantId();
    return {
      tenantId,
      user,
      ...overrides,
    };
  }, [user]);

  const isTourCompleted = useCallback((tourId, userId, tenantId = readTenantId()) => {
    if (shouldForceProductTour()) return false;
    return isTourCompletedInStorage(tourId, tenantId, userId);
  }, []);

  const markTourCompleted = useCallback((tourId) => {
    markTourCompletedInStorage(tourId, readTenantId(), user?.id);
  }, [user]);

  const stopTour = useCallback(() => {
    setPendingTourId(null);
  }, []);

  const startTour = useCallback(
    (tourId, { force = false, skipCompletedCheck = false } = {}) => {
      if (!user) return false;

      const ctx = buildTourContext();
      const resolvedId = tourId ?? resolveTourId(ctx);
      const tenantId = ctx.tenantId;

      if (!skipCompletedCheck && !force && !shouldForceProductTour()) {
        if (isTourCompleted(resolvedId, user.id, tenantId)) {
          return false;
        }
      }

      setPendingTourId({
        id: resolvedId,
        force: force || shouldForceProductTour(),
      });
      return true;
    },
    [buildTourContext, isTourCompleted, user],
  );

  const replayTour = useCallback(
    (tourId) => {
      if (!user) return false;
      const ctx = buildTourContext();
      const resolvedId = tourId ?? resolveTourId(ctx);
      return startTour(resolvedId, { force: true, skipCompletedCheck: true });
    },
    [buildTourContext, startTour, user],
  );

  const value = useMemo(
    () => ({
      isTourActive,
      setIsTourActive,
      pendingTourId,
      setPendingTourId,
      buildTourContext,
      isTourCompleted,
      markTourCompleted,
      startTour,
      stopTour,
      replayTour,
    }),
    [
      isTourActive,
      pendingTourId,
      buildTourContext,
      isTourCompleted,
      markTourCompleted,
      startTour,
      stopTour,
      replayTour,
    ],
  );

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      <ProductTourHost />
    </ProductTourContext.Provider>
  );
};

export const useProductTour = () => {
  const context = useContext(ProductTourContext);
  if (!context) {
    throw new Error('useProductTour must be used within a ProductTourProvider');
  }
  return context;
};
